import type { DashboardOverview } from "@/api/custom_types";
import { PageLayout } from "@/components";
import { defaultDashboardOverview } from "@/constants/defaults";
import { useApiError } from "@/hooks";
import { useTranslation } from "@/hooks/useTranslation";
import { useGetDashboardOverview } from "@/services";
import { userIntentAtom } from "@/store/login";
import { timestampsAtom } from "@/store/timestampAtom";
import { Typography } from "@st-marketplace/ui";
import { useAtom, useAtomValue } from "jotai";
import { type FC, useEffect, useMemo, useState } from "react";

import DashboardBothView from "./DashboardBothView";
import DashboardBuyView from "./DashboardBuyView";
import DashboardFilterSelector from "./DashboardFilterSelector";
import DashboardSellView from "./DashboardSellView";

function useMinutesSince(startMs: number | null) {
	const [minutes, setMinutes] = useState(0);

	useEffect(() => {
		if (startMs === null) return;
		const update = () => {
			setMinutes(Math.floor((Date.now() - startMs) / 60_000));
		};

		update();
		const iv = setInterval(update, 60_000);
		return () => clearInterval(iv);
	}, [startMs]);

	return minutes;
}

const Dashboard: FC = () => {
	const { t } = useTranslation();
	const { getErrorInterface } = useApiError();
	const [{ start_datetime, end_datetime }] = useAtom(timestampsAtom);
	const userIntent = useAtomValue(userIntentAtom);

	const [selectedFilter, setSelectedFilter] = useState<string>(
		start_datetime.toISOString(),
	);

	const query = useMemo(() => {
		return {
			start_datetime: selectedFilter,
			end_datetime: end_datetime.toISOString(),
		};
	}, [selectedFilter, end_datetime]);

	const {
		data,
		error: dashboardOverviewError,
		isLoading,
		dataUpdatedAt,
		refetch,
	} = useGetDashboardOverview({ query }, { refetchInterval: 1 * 60 * 1000 }); // 1 minutes

	const [firstUpdatedMs, setFirstUpdatedMs] = useState<number | null>(null);
	useEffect(() => {
		if (dataUpdatedAt && firstUpdatedMs === null) {
			setFirstUpdatedMs(dataUpdatedAt);
		}
	}, [dataUpdatedAt, firstUpdatedMs]);

	const minutesSinceFirst = useMinutesSince(firstUpdatedMs);

	const dashboardData: DashboardOverview = data ?? defaultDashboardOverview;

	const error = dashboardOverviewError
		? getErrorInterface(dashboardOverviewError)
		: null;

	return (
		<PageLayout>
			<div className="mb-6">
				<Typography variant="title-md" as="h1">
					{t("home.title")}
				</Typography>
				<Typography variant="caption" as="p" className="text-secondary-icon">
					{t("home.lastUpdate", { time: minutesSinceFirst })}
				</Typography>
			</div>

			{/* Each mode composes the same building blocks. */}
			{userIntent === "BUY" && (
				<DashboardBuyView
					data={dashboardData}
					loading={isLoading}
					error={error}
					refetch={refetch}
					filter={
						<DashboardFilterSelector
							selectedFilter={selectedFilter}
							onChange={setSelectedFilter}
						/>
					}
				/>
			)}

			{userIntent === "SELL" && (
				<DashboardSellView
					data={dashboardData}
					loading={isLoading}
					error={error}
					refetch={refetch}
					filter={
						<DashboardFilterSelector
							selectedFilter={selectedFilter}
							onChange={setSelectedFilter}
						/>
					}
				/>
			)}

			{userIntent === "BOTH" && (
				<DashboardBothView
					data={dashboardData}
					loading={isLoading}
					error={error}
					refetch={refetch}
					filter={
						<DashboardFilterSelector
							selectedFilter={selectedFilter}
							onChange={setSelectedFilter}
						/>
					}
				/>
			)}

			<div className="h-24" />
		</PageLayout>
	);
};

export default Dashboard;
